import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { CalendarIcon, Plus, X, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { cn, formatTurkishPhoneForDatabase } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logActivity } from '@/lib/activity-log';
import { useLocation } from '@/contexts/LocationContext';
import type { Database } from '@/integrations/supabase/types';

type CustomerColor = Database['public']['Enums']['customer_color_enum'];
type ManualTag = Database['public']['Enums']['manual_tag_enum'];

interface CustomerFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  birth_date: Date | undefined;
  anniversary_date: Date | undefined;
  color: CustomerColor;
  tags_manual: ManualTag[];
}

interface AddCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCustomerAdded?: (customerId?: string) => void;
}

const normalizePhoneDigits = (value: string | null | undefined): string => {
  if (!value) return '';
  return value.replace(/\D/g, '');
};

const colorOptions: { value: CustomerColor; label: string; colorClass: string; info: string }[] = [
  { value: 'mavi', label: 'Mavi', colorClass: 'bg-blue-500', info: 'Standart Müşteri' },
  { value: 'kirmizi', label: 'Kırmızı', colorClass: 'bg-red-500', info: 'Dikkat!' },
  { value: 'yesil', label: 'Yeşil', colorClass: 'bg-green-500', info: 'Opsiyonel' },
  { value: 'sari', label: 'Sarı', colorClass: 'bg-yellow-500', info: 'Opsiyonel' },
  { value: 'siyah', label: 'Siyah', colorClass: 'bg-gray-900', info: 'Özel Müşteri Mobil Bildirim Gider' },
  { value: 'beyaz', label: 'Beyaz', colorClass: 'bg-slate-200', info: 'Çalışan' },
  { value: 'gold', label: 'Altın', colorClass: 'bg-yellow-600', info: 'Harcamaya göre VIP sistem tarafından otomatik atanır' },
];

const manualTagOptions: { value: ManualTag; label: string }[] = [
  { value: 'vip', label: 'VIP' },
  { value: 'sadik', label: 'Sadık Müşteri' },
  { value: 'sik_soran', label: 'Sık Soran' },
  { value: 'fiyat_duyarli', label: 'Fiyat Duyarlı' },
  { value: 'pazarlikci', label: 'Pazarlıkçı' },
  { value: 'memnuniyetsiz_gecmis', label: 'Memnuniyetsiz Geçmiş' },
  { value: 'servis_bekliyor', label: 'Servis Bekliyor' },
  { value: 'montaj_bekliyor', label: 'Montaj Bekliyor' },
  { value: 'kurumsal', label: 'Kurumsal' },
  { value: 'taksit_agirlikli', label: 'Taksit Ağırlıklı' },
  { value: 'aksesuar_odakli', label: 'Aksesuar Odaklı' },
  { value: 'yakini_es_dost', label: 'Yakını Eş/Dost' },
  { value: 'geri_aransin', label: 'Geri Aransın' },
  { value: 'kampanya_ilgili', label: 'Kampanya İlgili' },
];

export const AddCustomerModal = ({ isOpen, onClose, onCustomerAdded }: AddCustomerModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTags, setSelectedTags] = useState<ManualTag[]>([]);
  const { toast } = useToast();
  const { currentLocation } = useLocation();

  const form = useForm<CustomerFormData>({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      birth_date: undefined,
      anniversary_date: undefined,
      color: 'mavi',
      tags_manual: [],
    },
  });

  const handleTagToggle = (tag: ManualTag) => {
    setSelectedTags(prev => {
      const newTags = prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag];
      form.setValue('tags_manual', newTags);
      return newTags;
    });
  };

  const handleSubmit = async (data: CustomerFormData) => {
    setIsSubmitting(true);
    
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) {
        throw new Error('Oturum bulunamadı');
      }

      // Get user's company_id
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('company_id')
        .eq('id', session.session.user.id)
        .single();

      if (!userProfile?.company_id) {
        throw new Error('Şirket bilgisi bulunamadı');
      }

      const formattedPhone = formatTurkishPhoneForDatabase(data.phone.trim());
      const normalizedPhone = normalizePhoneDigits(formattedPhone);

      if (normalizedPhone) {
        const { data: existingCustomers, error: existingCustomersError } = await supabase
          .from('customers')
          .select('id, first_name, last_name, phone')
          .eq('company_id', userProfile.company_id)
          .not('phone', 'is', null);

        if (existingCustomersError) {
          throw existingCustomersError;
        }

        const existingCustomer = (existingCustomers || []).find(
          (customer) => normalizePhoneDigits(customer.phone) === normalizedPhone
        );

        if (existingCustomer) {
          const existingName = `${existingCustomer.first_name || ''} ${existingCustomer.last_name || ''}`
            .trim() || 'İsimsiz Müşteri';

          toast({
            title: 'Müşteri zaten mevcut',
            description: `${existingCustomer.phone || data.phone} numarası ${existingName} için kayıtlı. Yeni kayıt oluşturulmadı.`,
            variant: 'destructive',
          });

          form.reset();
          setSelectedTags([]);
          onCustomerAdded?.(existingCustomer.id);
          onClose();
          return;
        }
      }

      const insertData = {
        first_name: data.first_name.trim() || null,
        last_name: data.last_name.trim() || null,
        email: data.email.trim() || null,
        phone: formattedPhone,
        birth_date: data.birth_date ? format(data.birth_date, 'yyyy-MM-dd') : null,
        anniversary_date: data.anniversary_date ? format(data.anniversary_date, 'yyyy-MM-dd') : null,
        color: data.color,
        tags_manual: selectedTags,
        company_id: userProfile.company_id,
      };

      const { data: newCustomer, error } = await supabase
        .from('customers')
        .insert([insertData])
        .select('id')
        .single();

      if (error) throw error;

      // Log activity
      if (userProfile?.company_id) {
        await logActivity({
          action: 'create',
          entity: 'customer',
          entityId: newCustomer.id,
          detail: {
            name: `${data.first_name.trim()} ${data.last_name.trim()}`.trim(),
            phone: data.phone.trim(),
            email: data.email.trim() || null,
          },
          locationId: currentLocation?.id || null,
          companyId: userProfile.company_id,
          userId: session.session.user.id,
        });
      }

      toast({
        title: 'Başarılı',
        description: 'Müşteri başarıyla eklendi.',
      });

      // Reset form and close modal
      form.reset();
      setSelectedTags([]);
      onCustomerAdded?.(newCustomer.id);
      onClose();

    } catch (error) {
      console.error('Error adding customer:', error);
      toast({
        title: 'Hata',
        description: error instanceof Error ? error.message : 'Müşteri eklenirken hata oluştu.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      form.reset();
      setSelectedTags([]);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="glass-panel-strong max-w-2xl border-white/30">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-xl font-semibold text-foreground flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
              <Plus className="w-4 h-4 text-accent" />
            </div>
            Yeni Müşteri Ekle
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Yeni müşteri bilgilerini doldurun. Zorunlu alanlar işaretlenmiştir.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground border-b border-white/10 pb-2">
                Temel Bilgiler
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="first_name"
                  rules={{ required: 'Ad zorunludur' }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ad *</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Müşterinin adı"
                          className="glass-panel border-white/20"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Soyad</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Müşterinin soyadı"
                          className="glass-panel border-white/20"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  rules={{
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Geçerli bir e-posta adresi girin'
                    }
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-posta</FormLabel>
                      <FormControl>
                        <Input 
                          type="email"
                          placeholder="ornek@email.com"
                          className="glass-panel border-white/20"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefon</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="0555 123 45 67"
                          className="glass-panel border-white/20"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Dates */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground border-b border-white/10 pb-2">
                Önemli Tarihler
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="birth_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Doğum Tarihi</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal glass-panel border-white/20",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? (
                                format(field.value, "dd MMMM yyyy", { locale: tr })
                              ) : (
                                "Tarih seçin"
                              )}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 glass-panel-strong border-white/30">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={(date) =>
                              date > new Date() || date < new Date("1900-01-01")
                            }
                            initialFocus
                            locale={tr}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="anniversary_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Yıldönümü</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal glass-panel border-white/20",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? (
                                format(field.value, "dd MMMM yyyy", { locale: tr })
                              ) : (
                                "Tarih seçin"
                              )}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 glass-panel-strong border-white/30">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={(date) =>
                              date > new Date() || date < new Date("1900-01-01")
                            }
                            initialFocus
                            locale={tr}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Color Selection */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground border-b border-white/10 pb-2">
                Müşteri Rengi
              </h3>
              
              <FormField
                control={form.control}
                name="color"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className="glass-panel border-white/20">
                          <SelectValue>
                            <div className="flex items-center gap-2">
                              <div 
                                className={cn(
                                  "w-4 h-4 rounded-full",
                                  colorOptions.find(c => c.value === field.value)?.colorClass
                                )}
                              />
                              {colorOptions.find(c => c.value === field.value)?.label}
                            </div>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="glass-panel-strong border-white/30">
                          {colorOptions.map((color) => (
                            <SelectItem key={color.value} value={color.value}>
                              <div className="flex items-center gap-2">
                                <div className={cn("w-4 h-4 rounded-full", color.colorClass)} />
                                <span>{color.label}</span>
                                <span className="text-xs text-muted-foreground">- {color.info}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Manual Tags */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground border-b border-white/10 pb-2">
                Etiketler
              </h3>
              
              <div className="flex flex-wrap gap-2">
                {manualTagOptions.map((tag) => (
                  <Badge
                    key={tag.value}
                    variant={selectedTags.includes(tag.value) ? "default" : "outline"}
                    className={cn(
                      "cursor-pointer transition-all duration-200 hover:scale-105",
                      selectedTags.includes(tag.value) 
                        ? "bg-accent text-accent-foreground accent-glow" 
                        : "glass-panel border-white/20 hover:border-white/30"
                    )}
                    onClick={() => handleTagToggle(tag.value)}
                  >
                    {tag.label}
                    {selectedTags.includes(tag.value) && (
                      <X className="w-3 h-3 ml-1" />
                    )}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-white/10">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex-1"
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 gap-2"
              >
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {isSubmitting ? 'Ekleniyor...' : 'Müşteri Ekle'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
